'use client';

// Reusable modal for Warn / Suspend. The admin types a message that is
// included in the email + in-app notice the user receives. Used by the admin
// People table and the on-profile AdminProfileToolbar.
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

export default function ModerationDialog({
  open,
  onOpenChange,
  title,
  description,
  label,
  placeholder,
  confirmLabel,
  confirmClass,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description?: string;
  label: string;
  placeholder?: string;
  confirmLabel: string;
  confirmClass?: string;
  onConfirm: (message: string) => Promise<void> | void;
}) {
  const [message, setMessage] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  // Reset on each open.
  React.useEffect(() => {
    if (open) {
      setMessage('');
      setBusy(false);
    }
  }, [open]);

  async function handleConfirm() {
    if (!message.trim() || busy) return;
    setBusy(true);
    try {
      await onConfirm(message.trim());
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription className="text-sm text-muted-foreground">{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-2">
          <label htmlFor="moderation-message" className="block text-sm font-medium">{label}</label>
          <textarea
            id="moderation-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={placeholder}
            rows={4}
            autoFocus
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
          />
          <p className="text-xs text-muted-foreground">This message is sent to the user by email and in-app.</p>
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent/40 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy || !message.trim()}
            className={'rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 ' + (confirmClass || 'bg-primary hover:bg-primary/90')}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
